import { DobroListing } from 'shared/components/DobroListing';
import { DobroListingStatusPage } from 'shared/components/DobroListingStatusPage';
import { useFetchDobroListing } from './internal/useFetchDobroListing';
import { DobroListingPopupContainer } from '../DobroListingPopupContainer';
import { EStatus, IOffer } from 'shared/types/listing';
import { useCallback, useState } from 'react';
import { useToast } from '@cian/ui-kit';
import { EMoscow } from 'shared/types/form';

const REGION_LS_KEY = 'cian-dobro.region';
const STATUS_LS_KEY = 'cian-dobro.status';

function getRegionValue(): EMoscow | null {
  /* istanbul ignore next */
  if (!globalThis.localStorage) {
    return null;
  }

  const allRegions: string[] = Object.values(EMoscow);
  const lsValue = localStorage.getItem(REGION_LS_KEY) || '';

  return allRegions.includes(lsValue) ? (lsValue as EMoscow) : null;
}

function getStatusValue(): EStatus | null {
  /* istanbul ignore next */
  if (!globalThis.localStorage) {
    return null;
  }

  const allStatues: string[] = Object.values(EStatus);
  const status = localStorage.getItem(STATUS_LS_KEY) || '';

  return allStatues.includes(status) ? (status as EStatus) : null;
}

export function DobroListingContainer() {
  const { result, loading, updateOffer } = useFetchDobroListing();
  const [regionFilter, setRegionFilter] = useState(getRegionValue);
  const [statusFilter, setStatusFilter] = useState(getStatusValue);
  const [modalOffer, setModalOffer] = useState<IOffer>();
  const toastApi = useToast();

  const onClose = useCallback(() => {
    setModalOffer(undefined);
  }, [setModalOffer]);

  const wrappedSetRegionFilter = useCallback(
    (region: EMoscow | null) => {
      localStorage.setItem(REGION_LS_KEY, region || '');
      setRegionFilter(getRegionValue());
    },
    [setRegionFilter],
  );

  const wrappedSetStatusFilter = useCallback(
    (status: EStatus | null) => {
      localStorage.setItem(STATUS_LS_KEY, status || '');
      setStatusFilter(getStatusValue());
    },
    [setStatusFilter],
  );

  const onUpdate = useCallback(
    async (status: EStatus, comment: string) => {
      /* istanbul ignore if */
      if (!modalOffer) {
        return;
      }

      try {
        await updateOffer({
          id: modalOffer.id,
          status,
          comment,
        });

        toastApi.open({ type: 'success', text: 'Информация сохранена' });
      } catch (_) {
        toastApi.open({ type: 'error', text: 'Что-то пошло не так, попробуйте ещё раз' });
      }
    },
    [modalOffer, updateOffer, toastApi],
  );

  if (loading) {
    return <DobroListingStatusPage title="Загрузка заявок" />;
  }

  if (result === 'no_access') {
    return (
      <DobroListingStatusPage
        title="Нет доступа"
        subtitle="Для просмотра заявок необходимо авторизоваться"
        buttonText="Авторизоваться"
        buttonLink={`/authenticate/?returnUrl=${encodeURIComponent(window.location.href)}`}
      />
    );
  }

  if (result === 'error' || !result) {
    return (
      <DobroListingStatusPage
        title="Произошла ошибка"
        subtitle="Попробуйте обновить страницу"
        buttonText="Обновить"
        buttonLink={window.location.href}
      />
    );
  }

  let filteredOffers = result.offers;
  const anyRegionCount = filteredOffers.length;
  const fitlerByLocation = (location: string) => filteredOffers.filter(offer => offer.location === location);
  const filterByOther = () =>
    filteredOffers.filter(o => o.location !== EMoscow.Moscow && o.location !== EMoscow.MoscowRegion);
  const countByRegion: { [key in EMoscow]: number } = {
    [EMoscow.Moscow]: fitlerByLocation(EMoscow.Moscow).length,
    [EMoscow.MoscowRegion]: fitlerByLocation(EMoscow.MoscowRegion).length,
    [EMoscow.Other]: filterByOther().length,
  };
  if (regionFilter) {
    if (regionFilter === EMoscow.Other) {
      filteredOffers = filterByOther();
    } else {
      filteredOffers = fitlerByLocation(regionFilter);
    }
  }
  const anyStatusCount = filteredOffers.length;
  const filterByStatus = (status: EStatus) => filteredOffers.filter(o => o.status === status);
  const countByStatus = {
    [EStatus.New]: filterByStatus(EStatus.New).length,
    [EStatus.Phone_not_available]: filterByStatus(EStatus.Phone_not_available).length,
    [EStatus.Canceled]: filterByStatus(EStatus.Canceled).length,
    [EStatus.Confirming]: filterByStatus(EStatus.Confirming).length,
    [EStatus.Busy]: filterByStatus(EStatus.Busy).length,
    [EStatus.Available]: filterByStatus(EStatus.Available).length,
  };
  if (statusFilter) {
    filteredOffers = filterByStatus(statusFilter);
  }

  return (
    <>
      <DobroListing
        region={regionFilter}
        setRegion={wrappedSetRegionFilter}
        openModal={setModalOffer}
        offers={filteredOffers}
        status={statusFilter}
        setStatus={wrappedSetStatusFilter}
        anyRegionCount={anyRegionCount}
        countByRegion={countByRegion}
        anyStatusCount={anyStatusCount}
        countByStatus={countByStatus}
      />
      {modalOffer && <DobroListingPopupContainer offer={modalOffer} onClose={onClose} onUpdate={onUpdate} />}
    </>
  );
}
